import { type ReactNode, useReducer } from 'react'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import {
  ChevronUpIcon,
  ChevronDownIcon,
  HomeIcon,
  CaretDownIcon,
  BuildingStoreIcon,
} from '@shipt/design-system-icons'
import { NextLink } from '@/features/shared/elements/Link'
import { getRetailerUrl, routes } from '@shared/constants/routes'
import { getUniversalNavCategories } from '@/features/shop/services/UniversalCategories/utils/getUniversalNavCategories'
import { Body } from '@shipt/design-system-typography'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import {
  MEMBERSHIP_POPOVER_LINKS,
  SAME_DAY_DELIVERY_LINKS,
} from '@/features/shared/constants/layouts'
import { type Hyperlink, ABOUT_US_LINKS, SHOP_LINKS } from '@/features/shared/constants/hyperlinks'
import { AccountCreationButtons } from '@/features/shared/components/GuestUser/AccountCreationButtons'
import { useIsGuestUser, useStoreId, useUserId } from '@/features/account/services/User/hooks'
import { useZip } from '@/features/account/services/Zip/hooks'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { DynamicGlobalAddressModal } from '@/features/account/components/Modal/GlobalAddressModal/DynamicGlobalAddressModal'
import { Avatar } from '@shipt/design-system-avatar'
import { DynamicStoreInfoDrawer } from '@/features/shop/components/Drawer/StoreInfoDrawer/DynamicStoreInfoDrawer'
import { NextDeliveryWindowText } from '@/features/shared/components/Headers/NextDeliveryWindowText'
import { spacingPx, spacingRem } from '@/features/shared/theme/tokens'

type Props = { onClose: () => void; isGlobalMenu: boolean }

export const MobileMenu = ({ onClose, isGlobalMenu }: Props) => {
  const navCategories = getUniversalNavCategories()
  const { openDialog } = useHandleDialog()
  const isGuestUser = useIsGuestUser()
  const address = useGlobalDefaultShoppingAddress()
  const zip = useZip()
  const storeId = useStoreId()

  const { data: store } = useUserShoppingStore()

  const renderMenuItemLinks = (links: Hyperlink[]) =>
    links.map((link) => {
      const LinkComponent = link.external ? ExternalLink : NextLink
      return (
        <MenuItemLinkWrapper key={link.name} align="center" spacing="sm" padding={['lg', 0]}>
          {!!link.rectangular_image && (
            <Avatar
              size="sm"
              colorScheme="brand"
              type="image"
              image={
                <Image
                  src={link.rectangular_image}
                  alt={link.name}
                  style={{ objectFit: 'contain' }}
                  fallbackType="store"
                  width={32}
                  height={32}
                />
              }
            />
          )}
          <LinkComponent href={link.url} onClick={onClose}>
            <LinkItem size="md">{link.name}</LinkItem>
          </LinkComponent>
        </MenuItemLinkWrapper>
      )
    })

  const handleChangeAddress = () => {
    if (isGuestUser) {
      openDialog(DynamicGuestAddressModal, {
        startChangeStep: true,
        isCancellable: true,
        ...(!address.street1 && {
          contentLabel: 'Enter your delivery address',
          subheading: 'Item availability and price may vary by location.',
        }),
      })
    } else {
      openDialog(DynamicGlobalAddressModal, {
        selectedStoreId: storeId,
      })
    }
  }
  const shownLocation = address.street1 ?? zip

  const handleStoreInfoOnClick = () => {
    openDialog(DynamicStoreInfoDrawer, { defaultTab: 'storeInfo' })
  }

  return (
    <Container spacing="xxs">
      <MenuSection paddingTop="md" paddingLeft="xl" paddingBottom="lg">
        <TransparentButton onClick={handleChangeAddress} aria-label="Address button">
          <AddressRow spacing="xs" align="center">
            <Body size="md">Deliver to</Body>

            <Body isPII className="shownLocation" size="md">
              {shownLocation}
            </Body>
            <CaretDownIcon />
          </AddressRow>
        </TransparentButton>
      </MenuSection>
      <MenuSection paddingBottom="lg" paddingTop="lg">
        <NextLink href={routes.GLOBAL_HOMEPAGE.url} onClick={onClose}>
          <AccountMenuItem align="center" spacing="sm">
            <HomeIcon size="md" />
            <LinkItem as="span" size="md">
              Home
            </LinkItem>
          </AccountMenuItem>
        </NextLink>
      </MenuSection>
      {!isGlobalMenu && !!store && (
        <MenuSection padding={['lg']}>
          <Column spacing="xl">
            <Row spacing="xs">
              <NextLink href={getRetailerUrl(store.name)}>
                <ImageWrapper
                  $backgroundColor={store.mp_store_logo_background_color ?? ''}
                  align="center"
                  marginRight="sm"
                >
                  <Image
                    src={store.mp_store_logo_image_url ?? ''}
                    fallbackType="store"
                    alt={store.name}
                    width={36}
                    height={36}
                  />
                </ImageWrapper>
              </NextLink>
              <Column>
                <Body strong>{store.name}</Body>
                <NextDeliveryWindowText size="md" />
              </Column>
            </Row>
            <TransparentButton onClick={handleStoreInfoOnClick}>
              <Row spacing="sm">
                <BuildingStoreIcon size="md" />
                <LinkItem as="span" size="md">
                  Store info and pricing
                </LinkItem>
              </Row>
            </TransparentButton>
          </Column>
        </MenuSection>
      )}
      <MenuSection padding={['lg']}>
        <MenuItemContent title="Same-day delivery">
          {renderMenuItemLinks(SAME_DAY_DELIVERY_LINKS)}
        </MenuItemContent>
        <MenuItemContent title="Shop">{renderMenuItemLinks(SHOP_LINKS)}</MenuItemContent>
        <MenuItemContent title="Categories">
          <MenuItemLinkWrapper align="center" spacing="sm" padding={['lg', 0]}>
            <NextLink href={routes.UNGATED_CATEGORY_HUB.url} onClick={onClose}>
              <LinkItem size="md">View all categories</LinkItem>
            </NextLink>
          </MenuItemLinkWrapper>
          {navCategories.map((category) => (
            <MenuItemLinkWrapper key={category.id} align="center" spacing="sm" padding={['lg', 0]}>
              <Image
                src={category.image_url}
                fallbackType="category"
                alt={category.name}
                width={28}
                height={28}
              />
              <NextLink href={category.url} onClick={onClose}>
                <LinkItem size="md">{category.name}</LinkItem>
              </NextLink>
            </MenuItemLinkWrapper>
          ))}
        </MenuItemContent>
        <MenuItemContent title="Memberships">
          {renderMenuItemLinks(MEMBERSHIP_POPOVER_LINKS)}
        </MenuItemContent>
        <MenuItemContent title="About us">{renderMenuItemLinks(ABOUT_US_LINKS)}</MenuItemContent>
      </MenuSection>
      {!useUserId() && (
        <MenuSection padding={['lg']}>
          <Box margin={[0, 'auto']}>
            <AccountButtons />
          </Box>
        </MenuSection>
      )}
    </Container>
  )
}

const MenuItemContent = ({ title, children }: { title: string; children: ReactNode }) => {
  const [isOpen, toggleIsOpen] = useReducer((state) => !state, false)

  return (
    <ContentWrapper marginBottom="lg">
      <MenuItemButton onClick={toggleIsOpen} shouldAddPadding={isOpen}>
        <Body className="menu-item-title" strong={isOpen} size="md">
          {title}
        </Body>{' '}
        {isOpen ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
      </MenuItemButton>
      {!!isOpen && children}
    </ContentWrapper>
  )
}

const Container = styled(Column)`
  background: ${({ theme }) => theme.gray100};
`

const MenuSection = styled(Box)`
  background: ${({ theme }) => theme.white};
`

const AccountMenuItem = styled(Row)`
  padding: ${spacingPx('sm', 0, 'sm', 'lg')};
  border-left: 2px solid transparent;
`

const AccountButtons = styled(AccountCreationButtons)`
  max-width: 25rem;
`

const ContentWrapper = styled(Box)`
  &:last-child {
    margin-bottom: ${spacingRem('sm')};
  }

  & .menu-item-title:hover {
    font-weight: ${({ theme }) => theme.font.static.body.medium.strong.default.md.fontWeight};
  }
`

const MenuItemButton = styled(TransparentButton)<{ shouldAddPadding: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacingRem('sm')};
  padding-bottom: ${({ shouldAddPadding }) => (shouldAddPadding ? '8px' : '0')};
`

const MenuItemLinkWrapper = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.gray100};
`

const ExternalLink = styled.a`
  text-decoration: none;
`

const ImageWrapper = styled(Stack)<{
  $backgroundColor: string
}>`
  border-radius: 8px;
  border: 1px solid rgb(0 0 0 / 10%);
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  width: 2.5rem;
  height: 2.5rem;
`

const AddressRow = styled(Row)`
  .shownLocation {
    text-decoration: underline;
  }
`

const LinkItem = styled(Body)`
  &:hover {
    font-weight: ${({ theme }) => theme.font.static.body.medium.strong.default.md.fontWeight};
  }
`
